import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAlt,
  faTimes,
  faUpload,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import UpgradeModal from "./UpgradeModal";
import { useAppContext } from "../context/AppContext";
import { createDirectArtifactBootstrap } from "../features/research/utils/createPlanOneshotBootstrap";
import { apiForm } from "../services/apiClient";
import "./ArtifactEntryModal.scss";

const COPY = {
  guideline: {
    title: "조사 가이드라인 만들기",
    description: "관련 문서 또는 간단한 목적을 바탕으로 가이드라인을 만듭니다.",
    existingTitle: "보유중인 문서로 생성하기",
    existingDesc: "관련 문서를 업로드하거나 직접 입력해 가이드라인을 만듭니다.",
    newTitle: "목적만 입력하고 빠르게 생성하기",
    newDesc: "계획서 없이 조사 목적만 입력해 바로 가이드라인을 만듭니다.",
    goalHint: "조사 목적을 입력하면 내부 생성 컨텍스트로 바로 가이드라인을 만들어요.",
    goalPlaceholder: "예: 멤버십 혜택 인지와 실제 사용 경험을 인터뷰로 파악하고 개선 포인트를 도출한다",
    submitLabel: "가이드라인 바로 만들기",
  },
  survey: {
    title: "스크리닝 설문 만들기",
    description: "관련 문서 또는 간단한 목적을 바탕으로 스크리닝 설문을 만듭니다.",
    existingTitle: "보유중인 문서로 생성하기",
    existingDesc: "관련 문서를 업로드하거나 직접 입력해 스크리닝 설문을 만듭니다.",
    newTitle: "목적만 입력하고 빠르게 생성하기",
    newDesc: "계획서 없이 조사 목적만 입력해 바로 스크리닝 설문을 만듭니다.",
    goalHint: "선별하려는 대상과 조사 목적을 입력하면 바로 설문을 만들어요.",
    goalPlaceholder: "예: 최근 3개월 내 멤버십 앱 사용 경험이 있는 사용자를 선별한다",
    submitLabel: "스크리닝 설문 바로 만들기",
  },
};

const formatFileSize = (bytes = 0) => (
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)}MB`
    : `${Math.max(1, Math.ceil(bytes / 1024))}KB`
);

const ArtifactEntryModal = ({ isOpen, onClose, artifactKind = "guideline" }) => {
  const navigate = useNavigate();
  const {
    createProject,
    canCreateProject,
    loadProjects,
    workspaceLoaded,
  } = useAppContext();
  const copy = COPY[artifactKind] || COPY.guideline;

  const [step, setStep] = useState("fork");
  const [goal, setGoal] = useState("");
  const [documentText, setDocumentText] = useState("");
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [methodologies, setMethodologies] = useState(["심층 인터뷰"]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [upgradeModal, setUpgradeModal] = useState({ isOpen: false, reason: "project" });

  useEffect(() => {
    if (!isOpen) return;
    loadProjects();
    setStep("fork");
    setGoal("");
    setDocumentText("");
    setUploadedDocument(null);
    setMethodologies(["심층 인터뷰"]);
    setSubmitting(false);
    setUploadingDocument(false);
    setErrorMessage("");
  }, [artifactKind, isOpen, loadProjects]);

  if (!isOpen) return null;

  const handleCreateDirect = async ({ sourceText, sourceType }) => {
    setErrorMessage("");
    if (sourceText.trim().length < 10) {
      setErrorMessage("생성 근거는 최소 10자 이상 입력해주세요.");
      return;
    }
    setSubmitting(true);
    try {
      if (!workspaceLoaded) {
        const loadedProjects = await loadProjects();
        if (!Array.isArray(loadedProjects)) {
          setErrorMessage("프로젝트 정보를 확인하지 못했습니다. 다시 시도해주세요.");
          return;
        }
      }
      const result = await createDirectArtifactBootstrap({
        artifactKind,
        sourceText,
        sourceType,
        methodologies: artifactKind === "guideline" ? methodologies : [],
        createProject,
        canCreateProject,
      });
      if (!result.success) {
        if (result.upgradeReason) {
          setUpgradeModal({ isOpen: true, reason: result.upgradeReason });
          return;
        }
        setErrorMessage(result.error || "생성에 실패했습니다.");
        return;
      }
      onClose();
      navigate(`/study/${result.studySlug}`);
    } catch (error) {
      setErrorMessage(error?.message || "생성 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDocumentUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setErrorMessage("파일 크기는 25MB 이하여야 합니다.");
      return;
    }
    setErrorMessage("");
    setSubmitting(true);
    setUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await apiForm("/generator/upload-file", formData);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || "문서 텍스트를 추출하지 못했습니다.");
      }
      setUploadedDocument({
        fileName: data?.file_name || file.name,
        fileSize: data?.file_size || file.size,
        processedContent: data?.processed_content || "",
      });
      if (!data?.processed_content) {
        setErrorMessage("문서에서 사용할 수 있는 텍스트를 찾지 못했습니다.");
      }
    } catch (error) {
      setErrorMessage(error?.message || "문서 업로드 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
      setUploadingDocument(false);
      event.target.value = "";
    }
  };

  const documentSourceText = [
    uploadedDocument?.processedContent,
    documentText,
  ].filter(Boolean).join("\n\n");
  const loadingMessage = uploadingDocument
    ? "업로드한 자료를 분석하고 있습니다."
    : artifactKind === "guideline"
      ? "가이드라인을 생성하고 있습니다."
      : "스크리닝 설문을 생성하고 있습니다.";

  const toggleMethodology = (methodology) => {
    setMethodologies((current) =>
      current.includes(methodology)
        ? current.filter((item) => item !== methodology)
        : [...current, methodology]
    );
  };

  return (
    <>
      <div className="artifact-entry-modal-overlay" onClick={onClose}>
        <div className="artifact-entry-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="artifact-entry-modal-header">
            <div>
              <h2>{copy.title}</h2>
              <p>{copy.description}</p>
            </div>
            <button type="button" className="artifact-entry-modal-close" onClick={onClose} title="닫기">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="artifact-entry-modal-body">
            {errorMessage ? (
              <div className="artifact-entry-modal-error" role="alert">
                {errorMessage}
              </div>
            ) : null}

            {step === "fork" ? (
              <div className="artifact-entry-modal-cards">
                <button
                  type="button"
                  className="artifact-entry-modal-card source"
                  onClick={() => {
                    setErrorMessage("");
                    setStep("document");
                  }}
                >
                  <div className="artifact-entry-modal-card-badge source">
                    <FontAwesomeIcon icon={faUpload} />
                    <span>자료 기반 생성</span>
                  </div>
                  <h3>{copy.existingTitle}</h3>
                  <p>{copy.existingDesc}</p>
                </button>
                <button
                  type="button"
                  className="artifact-entry-modal-card accent"
                  onClick={() => {
                    setErrorMessage("");
                    setStep("new");
                  }}
                >
                  <div className="artifact-entry-modal-card-badge accent">
                    <FontAwesomeIcon icon={faWandMagicSparkles} />
                    <span>빠른 생성</span>
                  </div>
                  <h3>{copy.newTitle}</h3>
                  <p>{copy.newDesc}</p>
                </button>
              </div>
            ) : null}

            {step === "new" ? (
              <div className="artifact-entry-modal-panel">
                <h3>{copy.newTitle}</h3>
                <p className="artifact-entry-modal-hint">{copy.goalHint}</p>
                <label className="artifact-entry-modal-field">
                  <span>조사 목표</span>
                  <textarea
                    value={goal}
                    onChange={(event) => setGoal(event.target.value)}
                    placeholder={copy.goalPlaceholder}
                    rows={5}
                    disabled={submitting}
                  />
                </label>
                <div className="artifact-entry-modal-actions">
                  <button type="button" className="secondary" onClick={() => setStep("fork")} disabled={submitting}>
                    이전
                  </button>
                  <button
                    type="button"
                    className="primary"
                    onClick={() => handleCreateDirect({ sourceText: goal, sourceType: "quick_goal" })}
                    disabled={submitting || goal.trim().length < 10}
                  >
                    {submitting ? "생성 준비 중..." : copy.submitLabel}
                  </button>
                </div>
              </div>
            ) : null}

            {step === "document" ? (
              <div className="artifact-entry-modal-panel">
                <h3>{copy.existingTitle}</h3>
                <p className="artifact-entry-modal-hint">
                  관련 문서를 업로드하거나 내용을 직접 입력하면, 추출된 텍스트를 바탕으로 생성합니다.
                </p>
                <label className="artifact-entry-modal-upload">
                  <FontAwesomeIcon icon={faUpload} />
                  <span>{uploadingDocument ? "업로드한 자료를 분석하고 있습니다." : "자료 업로드 (PDF, TXT, MD, DOCX 또는 이미지 · 최대 25MB)"}</span>
                  <input
                    type="file"
                    accept="application/pdf,text/plain,text/markdown,.txt,.md,.docx,image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleDocumentUpload}
                    disabled={submitting}
                  />
                </label>
                {uploadedDocument ? (
                  <div className="artifact-entry-modal-uploaded-file">
                    <FontAwesomeIcon icon={faFileAlt} />
                    <span>
                      <strong>{uploadedDocument.fileName}</strong>
                      <em>{formatFileSize(uploadedDocument.fileSize)} · 텍스트를 추출했습니다.</em>
                    </span>
                    <button type="button" onClick={() => setUploadedDocument(null)} disabled={submitting} aria-label="업로드한 자료 제거">
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ) : null}
                <label className="artifact-entry-modal-field">
                  <span>직접 입력하기</span>
                  <textarea
                    value={documentText}
                    onChange={(event) => setDocumentText(event.target.value)}
                    placeholder="브리프·회의록·기획 문서 등 생성에 참고할 내용을 붙여넣어 주세요."
                    rows={9}
                    disabled={submitting}
                  />
                </label>
                {artifactKind === "guideline" ? (
                  <div className="artifact-entry-modal-methodologies">
                    <span>가이드라인 유형</span>
                    {["심층 인터뷰", "사용성 테스트"].map((methodology) => (
                      <label key={methodology}>
                        <input
                          type="checkbox"
                          checked={methodologies.includes(methodology)}
                          onChange={() => toggleMethodology(methodology)}
                          disabled={submitting}
                        />
                        {methodology}
                      </label>
                    ))}
                  </div>
                ) : null}
                <div className="artifact-entry-modal-actions">
                  <button type="button" className="secondary" onClick={() => setStep("fork")} disabled={submitting}>
                    이전
                  </button>
                  <button
                    type="button"
                    className="primary"
                    onClick={() => handleCreateDirect({ sourceText: documentSourceText, sourceType: "document" })}
                    disabled={
                      submitting ||
                      documentSourceText.trim().length < 10 ||
                      (artifactKind === "guideline" && methodologies.length === 0)
                    }
                  >
                    {submitting ? "생성 준비 중..." : copy.submitLabel}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {submitting ? (
        <div className="artifact-entry-modal-loading" role="status" aria-live="polite">
          <span className="artifact-entry-modal-loading__spinner" aria-hidden="true" />
          <strong>{loadingMessage}</strong>
        </div>
      ) : null}

      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        reason={upgradeModal.reason}
        onClose={() => setUpgradeModal({ isOpen: false, reason: "project" })}
      />
    </>
  );
};

export default ArtifactEntryModal;
